/**
 * Action Inbox Bell Component
 *
 * Header bell icon with badge count showing pending agent actions.
 * Includes dropdown preview of recent actions with link to full inbox.
 *
 * Date: 2026-02-18
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Bell, ExternalLink, AlertCircle, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  getPendingActions,
  getPendingCount,
  getPriorityColor,
  type AgentAction,
} from '@/services/agentActionService';

const POLL_INTERVAL = 60000; // 60 seconds

export const ActionInboxBell: React.FC = () => {
  const [count, setCount] = useState(0);
  const [recentActions, setRecentActions] = useState<AgentAction[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchCount = useCallback(async () => {
    try {
      const pendingCount = await getPendingCount();
      setCount(pendingCount);
    } catch (error) {
      console.error('Failed to fetch action count:', error);
    }
  }, []);

  const fetchRecentActions = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await getPendingActions({ limit: 5 });
      setRecentActions(response.data);
    } catch (error) {
      console.error('Failed to fetch recent actions:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading]);

  // Initial fetch and polling
  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchCount]);

  // Fetch recent actions when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchRecentActions();
    }
  }, [isOpen, fetchRecentActions]);

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getPriorityIcon = (priority: string) => {
    if (priority === 'critical' || priority === 'high') {
      return <AlertCircle className="h-4 w-4 text-red-500" />;
    }
    return <Info className="h-4 w-4 text-muted-foreground" />;
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button className="relative p-2 rounded-full hover:bg-secondary">
          <Bell className="h-5 w-5 text-muted-foreground" />
          {count > 0 && (
            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center">
              <Badge
                variant="destructive"
                className="h-5 min-w-5 px-1 text-xs font-bold rounded-full"
              >
                {count > 99 ? '99+' : count}
              </Badge>
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80" align="end">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Agent Suggestions</span>
          {count > 0 && (
            <Badge variant="secondary" className="ml-2">
              {count} pending
            </Badge>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {isLoading && recentActions.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            Loading...
          </div>
        ) : recentActions.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No pending actions
          </div>
        ) : (
          <>
            {recentActions.map((action) => (
              <DropdownMenuItem
                key={action.id}
                className="flex flex-col items-start gap-1 p-3 cursor-pointer"
                asChild
              >
                <Link to={`/insights/inbox?action=${action.id}`}>
                  <div className="flex items-start gap-2 w-full">
                    {getPriorityIcon(action.priority)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm truncate">
                          {action.title}
                        </span>
                        <Badge
                          variant="outline"
                          className={`text-xs text-white ${getPriorityColor(action.priority)}`}
                        >
                          {action.priority}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                        <span>{action.agent_name}</span>
                        <span>•</span>
                        <span>{formatTimeAgo(action.created_at)}</span>
                        {action.site_name && (
                          <>
                            <span>•</span>
                            <span className="truncate">{action.site_name}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              </DropdownMenuItem>
            ))}
          </>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link
            to="/insights/inbox"
            className="flex items-center justify-center gap-2 text-sm font-medium text-primary"
          >
            View All Actions
            <ExternalLink className="h-3 w-3" />
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
